import { useCallback, useEffect, useMemo, useRef } from 'react';

import { ApolloError, type FetchResult, useApolloClient } from '@apollo/client';

import type { NexoyaContentFilterInput, NexoyaPortfolio } from '../../../../types';

import { useTeam } from '../../../../context/TeamProvider';
import {
  useCreateContentLabelCategory,
  useDeleteContentLabelCategory,
  useUpdateContentLabelCategory,
} from '../../../../graphql/contentLabels/mutationContentLabelCategory';
import {
  useCreateContentLabelCategoryValue,
  useDeleteContentLabelCategoryValue,
  useUpdateContentLabelCategoryValue,
} from '../../../../graphql/contentLabels/mutationContentLabelCategoryValue';
import {
  useManuallyAssignContentLabel,
  useRemoveContentLabelManualAssignments,
} from '../../../../graphql/contentLabels/mutationManualAssignment';
import {
  LIST_CONTENT_LABEL_CATEGORIES_QUERY,
  type ListContentLabelCategoriesResponse,
  useContentLabelCategoriesQuery,
} from '../../../../graphql/contentLabels/queryContentLabelCategories';
import {
  type ContentLabelRulePreviewResponse,
  useContentLabelRulePreviewQuery,
} from '../../../../graphql/contentLabels/queryContentLabelRulePreview';
import { useContentLabelStageCountsQuery } from '../../../../graphql/contentLabels/queryContentLabelStageCounts';
import {
  type AdAccount,
  useLabelAdAccounts as useLabelAdAccountsQuery,
} from '../../../../graphql/contentLabels/queryLabelAdAccounts';
import {
  type ContentsByAssignmentStageConnection,
  useContentsByAssignmentStageQuery,
} from '../../../../graphql/contentLabels/queryContentsByAssignmentStage';
import { usePortfoliosQuery } from '../../../../graphql/portfolio/queryPortfolios';
import { useDebounce } from '../../../../hooks/useDebounce';
import { extractErrorMessage, getFormattedErrorMessage } from '../../../../utils/errorFormatting';
import { isActivePortfolio } from '../../../../utils/portfolioActivity';

import {
  ALL_PORTFOLIOS_SCOPE,
  type LabelAssignmentRow,
  type LabelCategory,
  type LabelCategoryDraft,
  type LabelCommandOutcome,
  type LabelFilterClause,
  type LabelPreviewRow,
  type LabelStage,
  type LabelStageCounts,
  type PortfolioScopeOption,
  type PortfolioScopeValue,
  type StagePageCursor,
  type StagePageInfo,
} from '../types';
import { useProviderNameTranslator } from '../useProviderNameTranslator';

import {
  toAssignableContentIds,
  toContentFilterInput,
  toLabelAssignmentRow,
  toLabelCategories,
  toLabelPreviewRow,
  toLabelStageCountsById,
  toLabelValueFiltersInput,
  unassignableContentIdsMessage,
} from './labelCategoryMapper';

/**
 * The ONLY seam between Content Labels v2 and its data.
 *
 * UI code imports data from here and nowhere else — never from
 * `src/graphql/contentLabels/` directly — so every component can be tested by
 * mocking this one module, and the wire (uuid ids, `ContentV2`, translation keys,
 * `ContentFilterInput`) never leaks past it: hooks answer in the domain types of
 * `../types`, mapped by `./labelCategoryMapper`.
 *
 * There are exactly two doors into it:
 * - SETTINGS — the Content Labels screen — imports these hooks directly. It is the
 *   only surface that WRITES (`useLabelCommands`) and the only one that reads
 *   stages, pages and rule previews.
 * - EVERY OTHER SURFACE (Performance, Optimize, Content, Filters) reads the
 *   categories through `src/hooks/useContentLabelCategories`, which wraps
 *   `useLabelCategories` and adds the v2 feature flag — including a real `skip`, so
 *   a team without the flag sends nothing.
 * Both go through the same `LIST_CONTENT_LABEL_CATEGORIES_QUERY` document, so there
 * is still one wire contract, one cache entry and one mapper: a category list read
 * on the Performance tab is the same normalized entity Settings just renamed.
 *
 * Reads return `{ data, loading, error }` where `loading` means "nothing to show
 * yet" (the first load only): a refetch keeps the last answer on screen. Writes go
 * through `useLabelCommands`, whose commands never throw.
 */

export type RepositoryResult<TData> = {
  data: TData;
  /** Nothing to show yet — the first load only. A refetch keeps the last answer. */
  loading: boolean;
  error?: Error;
};

export { ALL_PORTFOLIOS_SCOPE };
export type { PortfolioScopeOption, PortfolioScopeValue };

/**
 * A read's `refetch` as something a click handler can call: it takes no
 * arguments and returns nothing, so a caller cannot accidentally pass the click
 * event through as refetch variables.
 *
 * A failed retry lands in `error` like the first failure did; the promise itself
 * has nobody to report to, and an unhandled rejection out of a click handler is
 * a crash. (Apollo's `reobserve` already marks it handled, so the catch is belt
 * and braces, not what keeps the log quiet.)
 */
function useRetry(refetch: () => Promise<unknown>): () => void {
  return useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);
}

// ── portfolio scope ─────────────────────────────────────────────────────────

type PortfolioEdge = { node?: Pick<NexoyaPortfolio, 'portfolioId' | 'title' | 'endDate'> | null };

/**
 * `first: 500` on purpose — the default of 50 silently truncates the dropdown
 * for large teams, which is exactly the bug PortfolioMultiSelectCombobox avoids.
 *
 * ENDED PORTFOLIOS ARE LEFT OUT, by the Portfolios screen's own Active split
 * (`isActivePortfolio`). No `contentsCount`: there is none on the wire, and the
 * trigger counts from the stage totals instead (`useLabelStageCounts`).
 */
export function usePortfolioScopeOptions(): RepositoryResult<PortfolioScopeOption[]> {
  const { data, loading, error } = usePortfoliosQuery({ first: 500 });

  const options = useMemo<PortfolioScopeOption[]>(() => {
    const edges: PortfolioEdge[] = data?.portfolios?.edges ?? [];
    return edges
      .map((edge) => edge?.node)
      .filter(Boolean)
      .filter(isActivePortfolio)
      .map((node) => ({ portfolioId: node.portfolioId, title: node.title ?? `#${node.portfolioId}` }));
  }, [data]);

  return { data: options, loading: Boolean(loading) && !data, error };
}

// ── categories ──────────────────────────────────────────────────────────────

const NO_CATEGORIES: LabelCategory[] = [];

/**
 * The team's categories and their labels. `refetch` is the error state's retry.
 *
 * `skip` reads nothing and reports nothing (empty, not loading). It exists for the
 * seam outside Settings (`src/hooks/useContentLabelCategories`), which gates this
 * read on the Content Labels v2 flag: discarding the answer would leave the request
 * itself going out from every Performance / Optimize / Content page of every team.
 * Settings passes nothing and is unaffected.
 */
export function useLabelCategories({ skip = false }: { skip?: boolean } = {}): RepositoryResult<LabelCategory[]> & {
  refetch: () => void;
} {
  const { data, loading, error, refetch } = useContentLabelCategoriesQuery({ skip });

  const categories = useMemo(() => (data ? toLabelCategories(data.listContentLabelCategories) : NO_CATEGORIES), [data]);

  return { data: categories, loading: loading && !data, error, refetch: useRetry(refetch) };
}

// ── stage counts ────────────────────────────────────────────────────────────

/**
 * Every category's four stage totals in one scope, keyed by `labelCategoryId`.
 * `portfolioId` null is the team-wide scope. A category missing from `data` is
 * UNKNOWN (still loading), which is not the same as a category with 0s.
 *
 * A label mutation that moves contents between stages (or adds a category) asks
 * this again itself; Apollo keeps the last answer for the same variables while
 * it does, so the pills do not blank out on every assignment. A new scope has no
 * answer yet — Apollo's `data` belongs to the current variables only, so the
 * previous scope's numbers are never shown under the next one's name.
 *
 * `skip` reads nothing and reports nothing (`{}`, not loading): because null
 * means "the whole team", a page that has not decided its scope yet has to say
 * so rather than pass null.
 *
 * Mount it ONCE per scope and hand the result down: stage reads are not
 * deduplicated (see `STAGE_READ_CONTEXT`), so every mount of the same scope in
 * the same render sends a request of its own.
 *
 * `refetch` is the failed read's retry. It is load-bearing rather than a
 * convenience: the label sheet's preview WAITS while the scope total is null
 * (an empty portfolio cannot be told from a full one until it is known), so a
 * failed counts read leaves that preview loading for good with nothing the
 * reader can change to end it.
 */
export function useLabelStageCounts(
  portfolioId: number | null,
  { skip = false }: { skip?: boolean } = {},
): RepositoryResult<Record<string, LabelStageCounts>> & { refetch: () => void } {
  const { data, loading, error, refetch } = useContentLabelStageCountsQuery({ portfolioId, skip });

  const counts = useMemo(() => toLabelStageCountsById(data?.listContentLabelCategories), [data]);

  return { data: counts, loading: loading && !data, error, refetch: useRetry(refetch) };
}

// ── stage pages ─────────────────────────────────────────────────────────────

export type LabelStagePage = {
  rows: LabelAssignmentRow[];
  totalCount: number;
  totalPages: number;
  pageInfo: StagePageInfo;
};

const EMPTY_STAGE_PAGE: LabelStagePage = {
  rows: [],
  totalCount: 0,
  totalPages: 0,
  pageInfo: { hasNextPage: false, hasPreviousPage: false, startCursor: null, endCursor: null },
};

/**
 * One server page of one category's stage. Paging is the caller's: it passes the
 * cursor (`pageInfo.endCursor` forward, `pageInfo.startCursor` backward) and gets
 * that page alone back.
 *
 * `loading` is "nothing of THIS table to show yet"; `refreshing` is a page turn,
 * a page-size change or a post-mutation refetch, during which the last page of
 * the same table stays in `data`. A table is one (category, stage, portfolio):
 * the next page of it is a fair stand-in for the page on its way, another
 * portfolio's or stage's rows are not — they would sit under this table's pills,
 * tickable and assignable — so a new table starts from `loading` like a first
 * load, and so does one that was closed and opened again. `stage` null reads
 * nothing.
 *
 * A read the server REFUSED comes back as the EMPTY page with `error`, neither
 * loading nor refreshing: nothing is on its way any more, and passing the last
 * page off as the one that was asked for would put its rows under the wrong page
 * number and its cursors in the caller's pager. A refetch of the page on screen
 * that fails keeps that page (Apollo holds `data` for unchanged variables), so
 * the reader does not lose the rows they are working with. Either way `refetch`
 * asks again — the table's Retry, and the only way out of a refused read, whose
 * stage and cursor are the ones the reader asked for.
 */
export function useLabelStagePage({
  labelCategoryId,
  stage,
  portfolioId,
  pageSize,
  cursor,
}: {
  labelCategoryId: string;
  stage: LabelStage | null;
  portfolioId: number | null;
  pageSize: number;
  cursor: StagePageCursor;
}): RepositoryResult<LabelStagePage> & { refreshing: boolean; refetch: () => void } {
  const translateProviderName = useProviderNameTranslator();
  const { data, connection, loading, error, refetch } = useContentsByAssignmentStageQuery({
    contentLabelCategoryId: labelCategoryId,
    stage,
    portfolioId,
    pageSize,
    cursor,
  });

  // The document hook falls back to Apollo's `previousData`, which carries no
  // record of whose answer it was. So only an answer to the CURRENT variables is
  // taken from it (`data` is set), and the stand-in is kept here instead, together
  // with the table it belongs to.
  const tableKey = JSON.stringify([labelCategoryId, stage, portfolioId]);
  const lastAnswer = useRef<{ tableKey: string; connection: ContentsByAssignmentStageConnection | undefined } | null>(
    null,
  );
  useEffect(() => {
    if (data) lastAnswer.current = { tableKey, connection };
    // A table LEFT BEHIND — closed, or switched away from before it answered —
    // keeps no stand-in: reopening it is a fresh panel (page 1, nothing ticked),
    // and the page it was left on may predate a write that dropped the stage's
    // data, which is the very case where page 1 is no longer in the cache.
    else if (lastAnswer.current?.tableKey !== tableKey) lastAnswer.current = null;
  }, [data, connection, tableKey]);
  // Only a page still ON ITS WAY has a stand-in. Once the read for these
  // variables has failed, nothing is coming, and the page before it is not the
  // page that was asked for: its rows would sit under the new page's number with
  // its cursors driving the pager. A refetch of the page on SCREEN is the other
  // way round — Apollo keeps `data` for unchanged variables, so those rows stay.
  const shown = data
    ? connection
    : loading && lastAnswer.current?.tableKey === tableKey
      ? lastAnswer.current.connection
      : undefined;

  const page = useMemo<LabelStagePage>(() => {
    if (!shown) return EMPTY_STAGE_PAGE;
    return {
      rows: (shown.edges ?? []).map((edge) => toLabelAssignmentRow(edge.node, translateProviderName)),
      totalCount: shown.totalCount ?? 0,
      totalPages: shown.totalPages ?? 0,
      pageInfo: {
        hasNextPage: Boolean(shown.pageInfo?.hasNextPage),
        hasPreviousPage: Boolean(shown.pageInfo?.hasPreviousPage),
        startCursor: shown.pageInfo?.startCursor ?? null,
        endCursor: shown.pageInfo?.endCursor ?? null,
      },
    };
  }, [shown, translateProviderName]);

  return {
    data: page,
    loading: loading && !shown,
    refreshing: loading && Boolean(shown),
    error,
    refetch: useRetry(refetch),
  };
}

// ── rule preview ────────────────────────────────────────────────────────────

/** How long the draft has to stop changing before the preview asks the server. */
export const RULE_PREVIEW_DEBOUNCE_MS = 300;

const NO_PREVIEW_ROWS: LabelPreviewRow[] = [];

/**
 * The contents a label's DRAFT rule reaches in one portfolio, for the label sheet.
 *
 * Sends the RULE clauses only — never the exclusion clause: the sheet shows an
 * excluded content as an unticked row so it can be re-included, which it could
 * not do for a row the server filtered out.
 *
 * `skipped` (with empty `data`) when there is nothing honest to ask:
 * - no usable rule — `filters: []` would answer with every content;
 * - `scopeTotal === 0` — on an empty portfolio `inPortfolioOnly` stops
 *   restricting server-side and answers with the whole team's matches.
 * While `scopeTotal` is still unknown (null) the preview WAITS (loading, not
 * skipped) for the same reason: until the size is known, an empty portfolio
 * cannot be told apart from a full one.
 *
 * The draft is debounced (~300ms), so typing a keyword does not send a request
 * per keystroke; the last answer stays up meanwhile, flagged `refreshing`. Both are scoped to one
 * (category, label, portfolio): the sheet stays mounted when it is reopened for
 * another label, and switching label or portfolio is not typing — it asks
 * straight away and never shows the previous scope's rows.
 */
export function useLabelRulePreview({
  category,
  labelId,
  portfolioId,
  filters,
  scopeTotal,
}: {
  category: LabelCategory;
  labelId: string;
  portfolioId: number | null;
  filters: LabelFilterClause[];
  scopeTotal: number | null;
}): RepositoryResult<LabelPreviewRow[]> & { skipped: boolean; refreshing: boolean } {
  const { teamId } = useTeam();
  const translateProviderName = useProviderNameTranslator();

  const scopeKey = JSON.stringify([category?.labelCategoryId ?? null, labelId, portfolioId]);

  // Debounced as a STRING: the caller's `filters` is typically a fresh array
  // every render, and debouncing that identity would restart the timer on every
  // render — or, once it fires, re-render forever. Equal drafts are equal strings.
  // The scope rides along so a debounced rule from ANOTHER scope is recognised
  // and the current rule is used as-is instead.
  const ruleKey = useMemo(() => JSON.stringify(toContentFilterInput(filters)), [filters]);
  const debouncedDraft = useDebounce(JSON.stringify([scopeKey, ruleKey]), RULE_PREVIEW_DEBOUNCE_MS);
  const askedRuleKey = useMemo(() => {
    const [debouncedScopeKey, debouncedRuleKey] = JSON.parse(debouncedDraft) as [string, string];
    return debouncedScopeKey === scopeKey ? debouncedRuleKey : ruleKey;
  }, [debouncedDraft, scopeKey, ruleKey]);
  const settledRule = useMemo<NexoyaContentFilterInput[]>(() => JSON.parse(askedRuleKey), [askedRuleKey]);
  const debouncing = askedRuleKey !== ruleKey;

  const hasRule = ruleKey !== '[]';
  const skipped = !teamId || scopeTotal === 0 || !hasRule;

  // The last answer and the scope it answered for, so a new draft keeps the
  // previous rows up while it loads — but a new label or portfolio never shows
  // another scope's rows, and a failed or cleared rule leaves no stand-in.
  const lastAnswer = useRef<{ scopeKey: string; data: ContentLabelRulePreviewResponse } | null>(null);

  const {
    data,
    error: queryError,
    loading: fetching,
  } = useContentLabelRulePreviewQuery({
    portfolioId,
    filters: settledRule,
    // With no stand-in, the debounced rule is one that was cleared or failed: never re-asked.
    skip: skipped || scopeTotal == null || (debouncing && !lastAnswer.current),
  });

  useEffect(() => {
    if (data) lastAnswer.current = { scopeKey, data };
    else if (queryError || skipped) lastAnswer.current = null;
  }, [data, queryError, skipped, scopeKey]);
  const answer = data ?? (lastAnswer.current?.scopeKey === scopeKey ? lastAnswer.current.data : undefined);
  // A failure belongs to the draft that was asked; once that draft is edited away it is nobody's.
  const error = debouncing ? undefined : queryError;

  const rows = useMemo(
    () =>
      answer
        ? answer.filterContents.map((content) => toLabelPreviewRow(content, category, labelId, translateProviderName))
        : NO_PREVIEW_ROWS,
    [answer, category, labelId, translateProviderName],
  );

  if (skipped) return { data: NO_PREVIEW_ROWS, loading: false, refreshing: false, skipped: true, error: undefined };
  const pending = debouncing || fetching || scopeTotal == null;
  return {
    data: rows,
    loading: !answer && !error,
    refreshing: Boolean(answer) && !error && pending,
    skipped: false,
    error,
  };
}

// ── commands ────────────────────────────────────────────────────────────────

export type LabelCommands = {
  /** `ok.id` is the new `labelCategoryId`. */
  createCategory(draft: LabelCategoryDraft): Promise<LabelCommandOutcome>;
  updateCategory(labelCategoryId: string, draft: LabelCategoryDraft): Promise<LabelCommandOutcome>;
  deleteCategory(labelCategoryId: string): Promise<LabelCommandOutcome>;
  /** `ok.id` is the new `labelId`. Created without a rule. */
  createLabel(labelCategoryId: string, name: string): Promise<LabelCommandOutcome>;
  /** Never sends the rule, so a rename cannot wipe it. */
  renameLabel(labelCategoryId: string, labelId: string, name: string): Promise<LabelCommandOutcome>;
  updateLabelRules(
    labelCategoryId: string,
    labelId: string,
    rules: { filters: LabelFilterClause[]; excludedContentIds: string[] },
  ): Promise<LabelCommandOutcome>;
  deleteLabel(labelCategoryId: string, labelId: string): Promise<LabelCommandOutcome>;
  assignLabel(labelCategoryId: string, labelId: string, contentIds: string[]): Promise<LabelCommandOutcome>;
  /** `removeContentLabelManualAssignments`: hands the contents back to their rules. */
  removeLabels(labelCategoryId: string, contentIds: string[]): Promise<LabelCommandOutcome>;
  pending: { category: boolean; label: boolean; assignment: boolean };
};

const OK: LabelCommandOutcome = { status: 'ok' };
const DUPLICATE: LabelCommandOutcome = { status: 'duplicate' };
const failed = (message: string): LabelCommandOutcome => ({ status: 'failed', message });

/**
 * The backend's duplicate-name error has no agreed code yet, so both signals it
 * may send count: a `BAD_USER_INPUT` code, or a message that says so.
 */
const DUPLICATE_MESSAGE = /already exists|duplicate|unique/i;

function isDuplicateNameError(error: unknown): boolean {
  const graphQLErrors = error instanceof ApolloError ? (error.graphQLErrors ?? []) : [];
  if (graphQLErrors.some((graphQLError) => graphQLError.extensions?.code === 'BAD_USER_INPUT')) return true;

  const messages = [
    extractErrorMessage(error),
    ...graphQLErrors.flatMap((graphQLError) => [graphQLError.message, String(graphQLError.extensions?.message ?? '')]),
  ];
  return messages.some((message) => DUPLICATE_MESSAGE.test(message));
}

/**
 * A rejected mutation as an outcome. Only the commands that send a NAME map
 * duplicates — a `BAD_USER_INPUT` on a delete or an assignment is not a name
 * clash, and calling it one would tell the user to rename something they never
 * named.
 */
function toFailureOutcome(error: unknown, { sendsName }: { sendsName: boolean }): LabelCommandOutcome {
  if (sendsName && isDuplicateNameError(error)) return DUPLICATE;
  return failed(getFormattedErrorMessage(error instanceof Error ? error : new Error(String(error))));
}

/** Runs one mutation and turns every way it can end into an outcome. Never throws. */
async function attempt<TData>(
  mutation: () => Promise<FetchResult<TData>>,
  toOutcome: (data: TData | null | undefined) => LabelCommandOutcome,
  { sendsName = false }: { sendsName?: boolean } = {},
): Promise<LabelCommandOutcome> {
  try {
    const { data } = await mutation();
    return toOutcome(data);
  } catch (error) {
    return toFailureOutcome(error, { sendsName });
  }
}

const sameName = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

const blankName = (what: 'category' | 'label') => failed(`Give the ${what} a name.`);

export function useLabelAdAccounts({ skip }: { skip?: boolean } = {}): {
  accounts: readonly AdAccount[];
  loading: boolean;
} {
  const translateName = useProviderNameTranslator();
  const { accounts, loading } = useLabelAdAccountsQuery({ skip });

  const named = useMemo(
    () => accounts.map((account) => ({ ...account, title: translateName(account.title) || account.title })),
    [accounts, translateName],
  );

  return { accounts: named, loading };
}

/**
 * Writes. Every command resolves to a `LabelCommandOutcome` and never throws:
 * - names are trimmed before they are sent, and a clash with a name in the
 *   CACHED list (trimmed, case-insensitive; within the category for labels) is
 *   answered `duplicate` without a request;
 * - a server duplicate (see `isDuplicateNameError`) is `duplicate` too;
 * - anything else is `failed` with a user-facing message, including a `false`
 *   answer from a delete or an assignment and content ids that cannot be sent.
 *
 * Cache effects (new cards and rows, stage reads dropped and the ones on screen
 * asked again) live in the mutation hooks, so a command only has to say how it
 * ended.
 */
export function useLabelCommands(): LabelCommands {
  const client = useApolloClient();
  const { teamId } = useTeam();

  const [createCategoryMutation, createCategoryState] = useCreateContentLabelCategory();
  const [updateCategoryMutation, updateCategoryState] = useUpdateContentLabelCategory();
  const [deleteCategoryMutation, deleteCategoryState] = useDeleteContentLabelCategory();
  const [createValueMutation, createValueState] = useCreateContentLabelCategoryValue();
  const [updateValueMutation, updateValueState] = useUpdateContentLabelCategoryValue();
  const [deleteValueMutation, deleteValueState] = useDeleteContentLabelCategoryValue();
  const [assignMutation, assignState] = useManuallyAssignContentLabel();
  const [removeMutation, removeState] = useRemoveContentLabelManualAssignments();

  /**
   * Read at CALL time, from the cache the page already fills — the freshest list
   * without subscribing this hook to it. Missing or incomplete means "cannot
   * tell", and the server gets the final say.
   */
  const cachedCategories = useCallback(() => {
    try {
      return (
        client.readQuery<ListContentLabelCategoriesResponse>({
          query: LIST_CONTENT_LABEL_CATEGORIES_QUERY,
          variables: { teamId },
        })?.listContentLabelCategories ?? []
      );
    } catch {
      return [];
    }
  }, [client, teamId]);

  const categoryNameTaken = useCallback(
    (name: string, exceptCategoryId?: string) =>
      cachedCategories().some(
        (category) => category.contentLabelCategoryId !== exceptCategoryId && sameName(category.name, name),
      ),
    [cachedCategories],
  );

  const labelNameTaken = useCallback(
    (labelCategoryId: string, name: string, exceptLabelId?: string) =>
      (cachedCategories().find((category) => category.contentLabelCategoryId === labelCategoryId)?.values ?? []).some(
        (value) => value.contentLabelCategoryValueId !== exceptLabelId && sameName(value.name, name),
      ),
    [cachedCategories],
  );

  const createCategory = useCallback<LabelCommands['createCategory']>(
    async ({ name: rawName, color }) => {
      const name = rawName.trim();
      if (!name) return blankName('category');
      if (categoryNameTaken(name)) return DUPLICATE;

      return attempt(
        () => createCategoryMutation({ name, color }),
        (data) => {
          const id = data?.createContentLabelCategory?.contentLabelCategoryId;
          return id ? { status: 'ok', id } : failed('The category could not be created.');
        },
        { sendsName: true },
      );
    },
    [categoryNameTaken, createCategoryMutation],
  );

  const updateCategory = useCallback<LabelCommands['updateCategory']>(
    async (labelCategoryId, { name: rawName, color }) => {
      const name = rawName.trim();
      if (!name) return blankName('category');
      if (categoryNameTaken(name, labelCategoryId)) return DUPLICATE;

      return attempt(
        () => updateCategoryMutation({ contentLabelCategoryId: labelCategoryId, name, color }),
        (data) =>
          data?.updateContentLabelCategory
            ? { status: 'ok', id: labelCategoryId }
            : failed('The category could not be saved.'),
        { sendsName: true },
      );
    },
    [categoryNameTaken, updateCategoryMutation],
  );

  const deleteCategory = useCallback<LabelCommands['deleteCategory']>(
    (labelCategoryId) =>
      attempt(
        () => deleteCategoryMutation({ contentLabelCategoryId: labelCategoryId }),
        (data) => (data?.deleteContentLabelCategory ? OK : failed('The category could not be deleted.')),
      ),
    [deleteCategoryMutation],
  );

  const createLabel = useCallback<LabelCommands['createLabel']>(
    async (labelCategoryId, rawName) => {
      const name = rawName.trim();
      if (!name) return blankName('label');
      if (labelNameTaken(labelCategoryId, name)) return DUPLICATE;

      return attempt(
        () => createValueMutation({ contentLabelCategoryId: labelCategoryId, name }),
        (data) => {
          const id = data?.createContentLabelCategoryValue?.contentLabelCategoryValueId;
          return id ? { status: 'ok', id } : failed('The label could not be created.');
        },
        { sendsName: true },
      );
    },
    [createValueMutation, labelNameTaken],
  );

  const renameLabel = useCallback<LabelCommands['renameLabel']>(
    async (labelCategoryId, labelId, rawName) => {
      const name = rawName.trim();
      if (!name) return blankName('label');
      if (labelNameTaken(labelCategoryId, name, labelId)) return DUPLICATE;

      return attempt(
        () =>
          updateValueMutation({ contentLabelCategoryId: labelCategoryId, contentLabelCategoryValueId: labelId, name }),
        (data) =>
          data?.updateContentLabelCategoryValue
            ? { status: 'ok', id: labelId }
            : failed('The label could not be renamed.'),
        { sendsName: true },
      );
    },
    [labelNameTaken, updateValueMutation],
  );

  const updateLabelRules = useCallback<LabelCommands['updateLabelRules']>(
    (labelCategoryId, labelId, { filters, excludedContentIds }) =>
      attempt(
        () =>
          updateValueMutation({
            contentLabelCategoryId: labelCategoryId,
            contentLabelCategoryValueId: labelId,
            // `null` when nothing usable is left: "no rule", never `[]`.
            filters: toLabelValueFiltersInput({ filters, excludedContentIds }),
          }),
        (data) =>
          data?.updateContentLabelCategoryValue
            ? { status: 'ok', id: labelId }
            : failed('The rule could not be saved.'),
      ),
    [updateValueMutation],
  );

  const deleteLabel = useCallback<LabelCommands['deleteLabel']>(
    (labelCategoryId, labelId) =>
      attempt(
        () => deleteValueMutation({ contentLabelCategoryId: labelCategoryId, contentLabelCategoryValueId: labelId }),
        (data) => (data?.deleteContentLabelCategoryValue ? OK : failed('The label could not be deleted.')),
      ),
    [deleteValueMutation],
  );

  const assignLabel = useCallback<LabelCommands['assignLabel']>(
    async (labelCategoryId, labelId, contentIds) => {
      // Checked here as well as in the hook so an unsendable selection is an
      // outcome, not a rejection, and no request is made.
      const assignable = toAssignableContentIds(contentIds);
      if (assignable.status === 'unassignable') return failed(unassignableContentIdsMessage(assignable.unconvertible));

      return attempt(
        () =>
          assignMutation({ contentLabelCategoryId: labelCategoryId, contentLabelCategoryValueId: labelId, contentIds }),
        (data) => (data?.manuallyAssignContentLabel ? OK : failed('The label could not be assigned.')),
      );
    },
    [assignMutation],
  );

  const removeLabels = useCallback<LabelCommands['removeLabels']>(
    async (labelCategoryId, contentIds) => {
      const assignable = toAssignableContentIds(contentIds);
      if (assignable.status === 'unassignable') {
        return failed(unassignableContentIdsMessage(assignable.unconvertible, 'remove content labels'));
      }

      return attempt(
        () => removeMutation({ contentLabelCategoryId: labelCategoryId, contentIds }),
        (data) => (data?.removeContentLabelManualAssignments ? OK : failed('The labels could not be removed.')),
      );
    },
    [removeMutation],
  );

  const categoryPending = createCategoryState.loading || updateCategoryState.loading || deleteCategoryState.loading;
  const labelPending = createValueState.loading || updateValueState.loading || deleteValueState.loading;
  const assignmentPending = assignState.loading || removeState.loading;

  return useMemo<LabelCommands>(
    () => ({
      createCategory,
      updateCategory,
      deleteCategory,
      createLabel,
      renameLabel,
      updateLabelRules,
      deleteLabel,
      assignLabel,
      removeLabels,
      pending: { category: categoryPending, label: labelPending, assignment: assignmentPending },
    }),
    [
      assignLabel,
      assignmentPending,
      categoryPending,
      createCategory,
      createLabel,
      deleteCategory,
      deleteLabel,
      labelPending,
      removeLabels,
      renameLabel,
      updateCategory,
      updateLabelRules,
    ],
  );
}
